import { getOrCreateI18n } from '@affine/i18n';
import {
  LocaleProvider,
  type LocaleService,
} from '@blocksuite/affine/shared/services';
import {
  type ViewExtensionContext,
  ViewExtensionProvider,
} from '@blocksuite/affine/ext-loader';
import type { Container } from '@blocksuite/global/di';
import type { ExtensionType } from '@blocksuite/store';
import { signal } from '@preact/signals-core';

export interface BlocksuiteI18nSource {
  language: string;
  on(event: 'languageChanged', listener: (language: string) => void): unknown;
  t(key: string, options?: Record<string, unknown>): unknown;
}

export function createBlocksuiteLocaleService(
  i18n: BlocksuiteI18nSource
): LocaleService {
  const language$ = signal(i18n.language || 'en');

  i18n.on('languageChanged', language => {
    language$.value = language;
  });

  return {
    language$,
    translate(key, fallback, options) {
      const language = language$.value;
      const value = i18n.t(key, {
        ...options,
        defaultValue: fallback,
        lng: language,
      });

      return typeof value === 'string' && value !== key ? value : fallback;
    },
  };
}

const localeService = createBlocksuiteLocaleService(
  getOrCreateI18n() as unknown as BlocksuiteI18nSource
);

export function getBlocksuiteLocaleService() {
  return localeService;
}

export const BlocksuiteLocaleExtension: ExtensionType = {
  setup: (di: Container) => {
    di.override(LocaleProvider, localeService);
  },
};

export class BlocksuiteLocaleViewExtension extends ViewExtensionProvider {
  override name = 'affine-locale-extension';

  override setup(context: ViewExtensionContext) {
    super.setup(context);
    context.register(BlocksuiteLocaleExtension);
  }
}
